import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useI18n } from "~/i18n";
import { resolveIpAssets } from "~/theme/resolve-theme";
import { LanguageSwitcher } from "./language-switcher";

interface HeaderProps {
  forceScrolled?: boolean;
}

export function Header({ forceScrolled = false }: HeaderProps) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { slug = "" } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const assets = resolveIpAssets(slug);
  const [scrolled, setScrolled] = useState(
    forceScrolled || window.scrollY > window.innerHeight * 0.4,
  );
  const [menuOpen, setMenuOpen] = useState(false);

  const isSubPage = slug ? pathname !== `/${slug}` : false;

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (isSubPage && slug) {
      e.preventDefault();
      window.location.href = `/${slug}${href}`;
    }
    setMenuOpen(false);
  };

  useEffect(() => {
    setScrolled(forceScrolled || window.scrollY > window.innerHeight * 0.4);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(forceScrolled || window.scrollY > window.innerHeight * 0.4);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [forceScrolled]);

  const navItems = [
    { key: "pv" as const, href: "#pv" },
    { key: "introduction" as const, href: "#introduction" },
    { key: "character" as const, href: "#character" },
    { key: "news" as const, href: "#news" },
    { key: "goods" as const, href: "#goods" },
  ];

  const logoSrc = assets.logos[locale as keyof typeof assets.logos] ?? assets.logos.ko;
  const logoAlt = assets.logoAlt[locale as keyof typeof assets.logoAlt] ?? assets.logoAlt.ko;

  return (
    <>
      <nav
        className={`header__nav hidden max-md:fixed max-md:inset-0 max-md:z-[999] max-md:flex max-md:flex-col max-md:items-center max-md:justify-center max-md:gap-10 max-md:bg-white max-md:transition-transform max-md:duration-[400ms] ${
          menuOpen ? "header__nav--open max-md:translate-x-0" : "max-md:translate-x-full"
        }`}
      >
        <button
          className="absolute top-5 right-6 w-6 h-6 flex items-center justify-center"
          onClick={() => setMenuOpen(false)}
          aria-label="close menu"
        >
          <span className="block w-full h-px bg-[var(--ip-text)] rotate-45 absolute" />
          <span className="block w-full h-px bg-[var(--ip-text)] -rotate-45 absolute" />
        </button>
        {navItems.map((item) => (
          <a
            key={item.key}
            href={item.href}
            className="header__nav-item relative font-['Inter',sans-serif] text-[14px] font-medium tracking-[0.2em] text-[var(--ip-text-muted)] transition-colors hover:text-[var(--ip-primary)]"
            onClick={(e) => handleNavClick(e, item.href)}
          >
            {t.nav[item.key]}
          </a>
        ))}
      </nav>

      <header
        className={`header sticky top-0 left-0 right-0 z-[100] px-10 transition-all duration-300 max-md:px-6 max-md:pl-2 ${
          scrolled
            ? "header--scrolled border-b border-[rgba(var(--ip-primary-glow),0.1)] bg-white py-4 shadow-[0_1px_24px_rgba(var(--ip-primary-glow),0.08)] max-md:py-[14px]"
            : "bg-transparent py-6 backdrop-blur-[12px] [mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)] max-md:py-5"
        }`}
      >
        <div className="header__inner mx-auto grid grid-cols-[1fr_auto_1fr] items-center max-md:flex max-md:justify-between">
          <a
            href="#top"
            className="header__logo flex items-center"
            onClick={(e) => {
              e.preventDefault();
              if (isSubPage && slug) {
                navigate(`/${slug}`);
              } else {
                window.scrollTo({ top: 0, behavior: "instant" });
              }
              setMenuOpen(false);
            }}
          >
            <img
              src={logoSrc}
              alt={logoAlt}
              className={`header__logo-img block ${slug === "oneroomta" ? "h-10" : "h-8"} w-auto transition-[filter] duration-300`}
            />
          </a>

          <nav className="header__nav flex items-center gap-9 max-md:hidden">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className="header__nav-item relative font-['Inter',sans-serif] text-[11px] font-medium tracking-[0.2em] text-[var(--ip-text-muted)] transition-colors hover:text-[var(--ip-primary)] after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-px after:scale-x-0 after:bg-[var(--ip-primary)] after:transition-transform after:content-[''] hover:after:scale-x-100"
                onClick={(e) => handleNavClick(e, item.href)}
              >
                {t.nav[item.key]}
              </a>
            ))}
          </nav>

          <div className="header__actions flex items-center justify-end gap-5">
            <LanguageSwitcher />

            <button
              className={`header__hamburger hidden w-6 flex-col gap-[5px] max-md:flex ${menuOpen ? "open [&>span:nth-child(1)]:translate-y-[6px] [&>span:nth-child(1)]:rotate-45 [&>span:nth-child(2)]:opacity-0 [&>span:nth-child(3)]:-translate-y-[6px] [&>span:nth-child(3)]:-rotate-45" : ""}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="menu"
            >
              <span className="block w-full h-px bg-[var(--ip-text)] transition-all duration-300" />
              <span className="block w-full h-px bg-[var(--ip-text)] transition-all duration-300" />
              <span className="block w-full h-px bg-[var(--ip-text)] transition-all duration-300" />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}
